import {
  CommunityEnum,
  type Detector,
  type EmotionType,
  type I18nRecord,
  type SortByType,
  type SubsPlanType,
  type SupportedLanguage,
} from '@/app/types';

export { API_ORIGIN, API_ORIGIN_VIDU, NETWORK_SETTINGS, resolveApiPath } from './network';
export {
  DEFAULT_STORY_GRID_SETTINGS,
  KEEP_CONTINUE_CREATE_PROMPT_STORAGE_KEY,
  normalizeStoryGridSettings,
  STORY_GRID_MAX_REFERENCE_COUNT,
  STORY_GRID_SETTINGS_OPTIONS,
  toolTaskSupportsTextPrompt,
} from './tool-capabilities';

import { SITE } from './site';

export const GA_ID: string = process.env.NEXT_PUBLIC_GA_ID ?? '';
// Google Tag Manager 容器 ID，可用环境变量覆盖，默认使用生产容器
export const GTM_ID: string = process.env.NEXT_PUBLIC_GTM_ID ?? 'GTM-MBGHXM6W';
export const GOOGLE_ONE_TAP_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_ONE_TAP_CLIENT_ID ?? '';

// 打 tag 的版本号（线上环境）
export const APP_VERSION: string = (process.env.NEXT_PUBLIC_APP_TAG ?? '').split('_')[0];

export const QRCODE_URL: string = process.env.NEXT_PUBLIC_QRCODE_URL ?? '';

export const OPTION_LABEL_MAP: Record<
  string,
  | 'style.optionGeneral'
  | 'style.optionAnime'
  | 'model.optionSpeed'
  | 'model.optionStandard'
  | 'movementAmplitude.optionAuto'
  | 'movementAmplitude.optionSmall'
  | 'movementAmplitude.optionMedium'
  | 'movementAmplitude.optionLarge'
  | 'stylePreference.optionCreative'
  | 'stylePreference.optionStable'
  | 'creationMode.optionFlash'
  | 'creationMode.optionCinematic'
  | 'creationMode.optionProAd'
  | 'codec.optionH265'
  | 'codec.optionH265Pro'
  | 'codec.optionH264'
  | 'codec.optionH264Pro'
> = {
  anime: 'style.optionAnime',

  // movement amplitude
  auto: 'movementAmplitude.optionAuto',

  // style preference
  creative: 'stylePreference.optionCreative', // 兼容旧版
  // style
  general: 'style.optionGeneral',
  large: 'movementAmplitude.optionLarge',
  medium: 'movementAmplitude.optionMedium',
  small: 'movementAmplitude.optionSmall',
  stable: 'stylePreference.optionStable',

  // model
  'vidu-1': 'model.optionSpeed',
  'vidu-high-performance': 'model.optionSpeed',
  'vidu-high-quality': 'model.optionStandard',
  pro: 'creationMode.optionCinematic',
  pro_ad: 'creationMode.optionProAd',
  speed: 'creationMode.optionFlash',

  // codec
  h265: 'codec.optionH265',
  h264: 'codec.optionH264',
  h265_pro: 'codec.optionH265Pro',
  h264_pro: 'codec.optionH264Pro',
};

export const VIDU_WELCOME_STORAGE_KEY = 'VIDU_WELCOME_CREATE_INPUT';

export const ACTIVITY_PAGE_FROM_KEY = 'activity_page_from';

/** 用户同意营销订阅的本地存储 key（登录/订阅弹窗等） */
export const CONSENT_MARKETING_MSG_KEY = 'VIDU_CONSENT_MARKETING_MSG';

export const TYPE_KEY_MAP: Record<
  string,
  | 'tabImg2Video'
  | 'tabCharacter2Video'
  | 'tabText2Video'
  | 'tabScene'
  | 'text2Audio'
  | 'tabReference2Image'
  | 'tabText2Image'
  | 'tabImageEdit'
  | 'tabVideoEdit'
  | 'tabAuto2Video'
  | 'tabRemix2Video'
  | 'tabVideo2Extend'
  | 'tabUpscaleImage'
  | 'tabUpscale'
  | 'tabFaceSwap'
  | 'tabRemake'
  | 'tabOneClick'
  | 'tabMusicVideo'
  | 'tabLipSync'
  | 'tabOfflineAvatar'
  | 'tabTextToSpeech'
  | 'tabClipCompose'
  | 'tabStoryGrid'
  | 'tabStyleTransfer'
  | 'tabMotionControl'
  | 'tabCharacterCreation'
> = {
  character2video: 'tabCharacter2Video',
  reference2image: 'tabReference2Image',
  text2image: 'tabText2Image',
  headtailimg2video: 'tabImg2Video',
  multi_frame: 'tabImg2Video',
  img2video: 'tabImg2Video',
  scene: 'tabScene',
  video_remix: 'tabRemix2Video',
  text2audio: 'text2Audio',
  text2video: 'tabText2Video',
  image_edit: 'tabImageEdit',
  video_edit: 'tabVideoEdit',
  clip_compose: 'tabClipCompose',
  auto2video: 'tabAuto2Video',
  remix2video: 'tabRemix2Video',
  video2extend: 'tabVideo2Extend',
  video2extail: 'tabVideo2Extend',
  upscale_image: 'tabUpscaleImage',
  upscale: 'tabUpscale',
  face_swap: 'tabFaceSwap',
  remake: 'tabRemake',
  one_click: 'tabOneClick',
  music_video: 'tabMusicVideo',
  lip_sync: 'tabLipSync',
  offline_avatar: 'tabOfflineAvatar',
  tts: 'tabTextToSpeech',
  short_story: 'tabStoryGrid',
  style_transfer: 'tabStyleTransfer',
  motion_control: 'tabMotionControl',
  character_creation: 'tabCharacterCreation',
};

export const MAX_PROMPT_LENGTH = 3000;
export const MAX_IMAGE_FILE_SIZE = 50 * 1024 * 1024; // 50MB，用户上传图片时最大可选择的文件大小，超过 10MB 会进行压缩（resize 到 4k 面积 & 转换为 webp）后再传给后端
export const MIN_IMAGE_AREA = 128 * 128;

export const MAX_IMAGE_RATIO = 4;
export const ASPECT_RATIO_TOLERANCE = 1.25; // 可以接受的首尾帧宽高比差异

// 不同分辨率的尺寸
export const RESOLUTION_MAP = {
  '512': 512 * 512,
  '720p': 1280 * 720,
  '768': 768 * 768,
  '1080p': 1920 * 1080,
  '2k': 2560 * 1440,
  '4k': 3840 * 2160,
};

export const SCENE = 'scene';

export const EN_FAQ_URL = '/help-center';
const GLOBAL_ZH_FAQ_URL = 'https://shengshu.feishu.cn/docx/Dtv8dbnUKo5glfxDoLXcEwzSnyd';
const CHINA_ZH_FAQ_URL = '/help-center';
export const ZH_FAQ_URL = SITE === 'china' ? CHINA_ZH_FAQ_URL : GLOBAL_ZH_FAQ_URL;

export const CURRENCY_MAP = {
  CNY: '¥',
  USD: '$',
};
export const CURRENCY = SITE === 'china' ? CURRENCY_MAP.CNY : CURRENCY_MAP.USD;

export const ONE_MINUTE = 1000 * 60; // 一分钟
export const ONE_HOUR = ONE_MINUTE * 60; // 一小时
export const ONE_DAY = ONE_HOUR * 24; // 一天
export const ONE_WEEK = ONE_DAY * 7; // 一周
export const ONE_MONTH = ONE_DAY * 30; // 1月

export const GLOBAL_API_URL = 'https://platform.vidu.com';
export const CHINA_API_URL = 'https://platform.vidu.cn';
export const API_URL = SITE === 'china' ? CHINA_API_URL : GLOBAL_API_URL;

export const CPP_URL =
  SITE === 'china'
    ? ''
    : 'https://docs.google.com/forms/d/e/1FAIpQLSfQ8zx4KwcS4Jv8DOmOaVcUKMTHvjSr1q5Fmv0Gyh-DTZUNXg/viewform?usp=dialog';
export const SHENGSHU_WEBSITE_URL = {
  en: 'https://www.shengshu.com/en',
  zh: 'https://www.shengshu.com/zh',
};

export const USER_BG = ['#4E7F97', '#3C679B', '#5E729A', '#629379'];

export const TRACE_EVENT_ID = {
  [CommunityEnum.ImageAsset]: 'inspiration_id',
  [CommunityEnum.MediaAsset]: 'inspiration_id',
  [CommunityEnum.ShortFilm]: 'film_id',
  [CommunityEnum.Tutorial]: 'tutorial_id',
  [CommunityEnum.PostReferences]: 'referece_id',
  [CommunityEnum.MiniVideo]: 'mini_video_id',
};

export const TRACE_EVENT_NAME = {
  [CommunityEnum.ImageAsset]: 'inspiration_interaction',
  [CommunityEnum.MediaAsset]: 'inspiration_interaction',
  [CommunityEnum.ShortFilm]: 'film_interaction',
  [CommunityEnum.Tutorial]: 'tutorials_interaction',
  [CommunityEnum.PostReferences]: 'shared_reference_interacation',
  [CommunityEnum.MiniVideo]: 'mini_video_interaction',
};

export const TAB_TYPE_MAP = {
  [CommunityEnum.ImageAsset]: 'inspiration_image',
  [CommunityEnum.MediaAsset]: 'inspiration',
  [CommunityEnum.ShortFilm]: 'short-film',
  [CommunityEnum.Tutorial]: 'tutorial',
  [CommunityEnum.PostReferences]: 'reference',
  [CommunityEnum.MiniVideo]: 'mini_video',
} as const;

export const IMAGE_NAME_PREFIX: I18nRecord<string> = {
  china: {
    zh: '图',
  },
  global: {
    de: 'Bild',
    en: 'image',
    es: 'imagen',
    fr: 'image',
    ja: '図',
    ko: '이미지',
    pt: 'Imagem',
    ru: 'изображение',
    zh: '图',
  },
};

export const VIDEO_NAME_PREFIX: I18nRecord<string> = {
  china: {
    zh: '视频',
  },
  global: {
    en: 'video',
    zh: '视频',
  },
};

export const HOVER_PLAY_DELAY = 100;

// 官方 Discord 链接
export const DISCORD_LINK = 'https://discord.gg/muRwC6PX8H';

// 基础 revalidate 时间, 用于接入sanity系统的页面增量更新
export const BASE_REVALIDATE_TIME = 60 * 60; // 1小时

export const NUMBER_IMG = [
  'https://image01.cf.vidu.studio/vidu/activity/0.7b39eb75.png',
  'https://image01.cf.vidu.studio/vidu/activity/1.d79417f9.png',
  'https://image01.cf.vidu.studio/vidu/activity/2.5ac0f015.png',
  'https://image01.cf.vidu.studio/vidu/activity/3.1a37c20d.png',
  'https://image01.cf.vidu.studio/vidu/activity/4.9718fcf6.png',
  'https://image01.cf.vidu.studio/vidu/activity/5.e448416b.png',
  'https://image01.cf.vidu.studio/vidu/activity/6.4a668844.png',
  'https://image01.cf.vidu.studio/vidu/activity/7.2a038fe4.png',
  'https://image01.cf.vidu.studio/vidu/activity/8.23248f0d.png',
  'https://image01.cf.vidu.studio/vidu/activity/9.762e32e9.png',
];

/**
 * 魔法色默认值
 * see more: https://github.com/woltapp/blurhash/tree/master/TypeScript
 */
export const BLUR_HASH_DEFAULT = {
  COLOR: 'UGCaSy1UL2.19Y.7M_jX00?HxZMw-oIAr?aJ',
  SIZE: 8,
} as const;

export const RECREATE_TASK_KEY = 'taskId';
export const RECREATE_INSPIRATION_KEY = 'inspirationId';
export const RECREATE_INSPIRATION_SOURCE_KEY = 'source';

// 应用下载链接配置
export const APP_DOWNLOAD_LINKS = {
  china: {
    android: 'https://www.vidu.cn/app-download', // 中国区 App Store 链接
    ios: 'https://apps.apple.com/cn/app/vidu-ai-pro/id6761521089', // 应用宝或其他中国区应用商店链接
  },
  global: {
    android: 'https://play.google.com/store/apps/details?id=com.vidu.agent.oversea',
    ios: 'https://apps.apple.com/us/app/vidu-pro/id6762187844',
  },
};

export const EXPLORE_ROUTE_PATHS = {
  ACTIVITY: '/explore/activity',
  INSPIRATION: '/explore/inspiration',
  SHORT_FILM: '/explore/short-film',
  TUTORIAL: '/explore/tutorial',
  REFERENCE: '/explore/reference',
};

export const MENTION_COLOR_MAP = {
  /** 主体 */
  element: '#2C80FF',
  /** 禁用 */
  forbidden: '#4C4C4C',
  /** 图片 */
  image: '#999BAC',
  /** 视频 */
  video: '#999BAC',
  /** 共享元素 */
  share_reference: '#958DFF',
} as const;

// 国内多语言
export const CHINA_LANGUAGES: SupportedLanguage[] = ['zh'];
// 国外多语言
export const GLOBAL_LANGUAGES: SupportedLanguage[] = ['en', 'zh', 'ja', 'ko', 'de', 'fr', 'es', 'pt', 'ru'];

export const GLOBAL_LANGUAGES_CONFIG: { label: string; value: SupportedLanguage }[] = [
  {
    label: 'English',
    value: 'en',
  },
  {
    label: '中文',
    value: 'zh',
  },
  {
    label: '日本語',
    value: 'ja',
  },
  {
    label: '한국어',
    value: 'ko',
  },
  {
    label: 'Deutsch',
    value: 'de',
  },
  {
    label: 'Français',
    value: 'fr',
  },
  {
    label: 'Español',
    value: 'es',
  },
  {
    label: 'Português',
    value: 'pt',
  },
  {
    label: 'Русский',
    value: 'ru',
  },
];

// 主体数量
export const REFERENCE_RIGHT_LIST: Record<SubsPlanType, number> = {
  free: 10,
  standard_v2: 50,
  premium_v2: 100,
  ultimate_v2: 300,
  team_v2: 1000,
};

export const MAX_REFERENCE_PROMPTS = 7;
export const MAX_IMG2V_FRAMES = 10;
/**
 * 任务进度存储键
 */
export const TASK_PROGRESS_STORAGE_KEY = 'VIDU_CREATING_TIME_LEFT';
/**
 * 本地存储数据分隔符
 */
export const LOCALSTORAGE_DATA_SPLIT = '###';

export enum NOT_FOUND_REASON {
  /** 功能不支持 */
  FEATURE_NOT_SUPPORTED = 'notFound.featureNotSupported',
  /** 404 */
  NORMAL_NOT_FOUND = 'notFound.normalNotFound',
}

export const IS_PROD = process.env.NEXT_PUBLIC_DEPLOY_ENV === 'prod';

export enum ScreenType {
  /**  { max: 767 } */
  SM = 'sm',
  /**  { max: 1023 } */
  MD = 'md',
  /**  { max: 1439 } */
  LG = 'lg',
  /**  { min: 1624 } */
  XL = 'xl',
  /**  { min: 1920 } */
  FHD = 'fhd',
  /**  { min: 2160 } */
  QHD = 'qhd',
  /**  { min: 2560 } */
  QHD_PLUS = 'qhd-plus',
  /** 默认 */
  DEFAULT = 'default',
}

export const HOME_PAGE = '/home';
export const SORT_ICON_MAP: Record<SortByType, string> =
  SITE === 'china'
    ? {
        time: 'https://image01-aliyun.vidu.zone/vidu/media-asset/sort_new-1307c467.webp',
        recommend: 'https://image01-aliyun.vidu.zone/vidu/media-asset/sort_hot-bbc86094.webp',
      }
    : {
        time: 'https://image01.cf.vidu.studio/vidu/media-asset/sort_new-1307c467.webp',
        recommend: 'https://image01.cf.vidu.studio/vidu/media-asset/sort_hot-bbc86094.webp',
      };

export const SEARCH_EMPTY_ICON =
  SITE === 'china'
    ? {
        selector: 'https://image01-aliyun.vidu.zone/vidu/media-asset/search_empty-baf539fd.webp',
        feed: 'https://image01-aliyun.vidu.zone/vidu/media-asset/search_empty-2e67b0b3.webp',
      }
    : {
        selector: 'https://image01.cf.vidu.studio/vidu/media-asset/search_empty-baf539fd.webp',
        feed: 'https://image01.cf.vidu.studio/vidu/media-asset/search_empty-2e67b0b3.webp',
      };

// 参考 https://en.wikipedia.org/wiki/List_of_file_signatures
export const detectors: Detector[] = [
  {
    type: 'image/png',
    check: (buffer) =>
      buffer[0] === 0x89 &&
      buffer[1] === 0x50 &&
      buffer[2] === 0x4e &&
      buffer[3] === 0x47 &&
      buffer[4] === 0x0d &&
      buffer[5] === 0x0a &&
      buffer[6] === 0x1a &&
      buffer[7] === 0x0a,
  },
  {
    type: 'image/jpeg',
    check: (buffer) => buffer[0] === 0xff && buffer[1] === 0xd8 && buffer[2] === 0xff,
  },
  {
    type: 'image/webp',
    // "RIFF????WEBP"
    check: (buffer) =>
      buffer[0] === 0x52 &&
      buffer[1] === 0x49 &&
      buffer[2] === 0x46 &&
      buffer[3] === 0x46 &&
      buffer[8] === 0x57 &&
      buffer[9] === 0x45 &&
      buffer[10] === 0x42 &&
      buffer[11] === 0x50,
  },
  {
    type: 'image/bmp',
    // "BM"
    check: (buffer) => buffer[0] === 0x42 && buffer[1] === 0x4d,
  },
];

export const ACCEPT_IMAGE_MIMETYPE: string[] = ['image/jpeg', 'image/png', 'image/webp'];
export const ACCEPT_VIDEO_MIMETYPE: string[] = ['video/mp4', 'video/quicktime'];

export enum NewbieTaskName {
  /** 图生2.0任务 */
  newbie_task_i2v_v2_credit = 'newbie_task_i2v_v2_credit',
  /** 图生Q1任务 */
  newbie_task_i2v_Q1_credit = 'newbie_task_i2v_Q1_credit',
  /** 参考生Q1任务 */
  newbie_task_c2v_Q1_credit = 'newbie_task_c2v_Q1_credit',
  /** 模板任务 */
  newbie_task_template_credit = 'newbie_task_template_credit',
  /** 图生q2任务 */
  newbie_task_i2v_Q2_credit = 'newbie_task_i2v_Q2_credit',
  /** 参考生q2任务 */
  newbie_task_c2v_Q2_credit = 'newbie_task_c2v_Q2_credit',
  /** 创建主体任务 */
  newbie_task_material_create_credit = 'newbie_task_material_create_credit',
  /** 使用主体任务 */
  newbie_task_use_material_credit = 'newbie_task_use_material_credit',
  /** 生图任务（多步骤任务「生图 → 上传图片创作视频」的第一步） */
  newbie_task_image_generate_credit = 'newbie_task_image_generate_credit',
  /** 上传图片创作视频任务（多步骤任务「生图 → 上传图片创作视频」的第二步） */
  newbie_task_image_to_video_credit = 'newbie_task_image_to_video_credit',
}

export enum NormalFreeCreditsTaskName {
  /** 新人任务 */
  new_user_credit = 'new_user_credit',
  /** 下载应用任务 */
  download_app_credit = 'download_app_credit',
  /** 问卷任务 */
  questionnaire_credit = 'questionnaire_credit',
  /** 订阅邮件任务 */
  subscribe_email_credit = 'subscribe_email_credit',
  /** 邀请码任务 */
  invitecodecatalyst_credit = 'invitecodecatalyst_credit',
  /** 每日签到任务 */
  every_day_credit = 'every_day_credit',
  /** 观看广告任务 */
  mission_watch_ad_credit = 'mission_watch_ad_credit',
  /** 取消续费原因问卷任务 */
  renewal_questionnaire_credit = 'renewal_questionnaire_credit',
}
/** 免费积分任务名称 */
export type FreeCreditsTaskName = NewbieTaskName | NormalFreeCreditsTaskName;

/**
 * 新功能键名枚举
 * 用于展示新功能高亮提示
 */
export enum NewFeatureKeys {
  QUICK_EDIT = 'quickEdit',
  QUICK_EDIT_ADD_IMAGE = 'quickEditAddImage',
  LIVE_SCREEN_SHARE = 'liveScreenShare',
  LIVE_TEXT_INPUT = 'liveTextInput',
}

export const BANNER_DELAY = 5000;

export const THINKING_GIF =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/agent-thinking-3607fd6c.gif'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/agent-thinking-3607fd6c.gif';

export const CHARACTER_IMAGE_EXAMPLE_ONE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/char-example1-934f1dfc.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/char-example1-934f1dfc.webp';

export const CHARACTER_IMAGE_EXAMPLE_TWO =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/char-example2-fd47e939.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/char-example2-fd47e939.webp';

export const MOTION_VIDEO_EXAMPLE_ONE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/motion-video-example1-70aeaa05.mp4'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/motion-video-example1-70aeaa05.mp4';

export const MOTION_VIDEO_EXAMPLE_TWO =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/motion-video-example2-5a3bffe2.mp4'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/motion-video-example2-5a3bffe2.mp4';

export const COPY_RIGHT = 'Copyright © 2026 Vidu®';

export * from './agent';
export * from './audio/audio-images';
export * from './audio/text2audio';
export * from './audio/tts/voice-list';
export * from './audio/voice-clone-reading-text';
export * from './aws';
export * from './breakpoints';
export * from './claw';
export * from './distribution';
export * from './enterprise-copy';
export * from './form-settings-config';
export * from './google-afs';
export * from './google-rewarded-ad';
export * from './network';
export * from './products-list-default';
export * from './seo';
export * from './site';
export * from './task-form';
export * from './theme';
export * from './timbreList';
export * from './upload-scene';
export * from './video-constants';

export const TTS_EMOTIONS: EmotionType[] = [
  {
    id: 'happy',
    labelKey: 'tools.textToSpeech.emotions.happy',
    tagBackgroundColor: 'rgba(255, 190, 92, 0.24)',
    tagTextColor: '#FFBE5C',
    emotionTextBackgroundColor: 'rgba(255, 190, 92, 0.16)',
  },
  {
    id: 'sad',
    labelKey: 'tools.textToSpeech.emotions.sad',
    tagBackgroundColor: 'rgba(242, 87, 118, 0.24)',
    tagTextColor: '#FF7692',
    emotionTextBackgroundColor: 'rgba(242, 87, 118, 0.16)',
  },
  {
    id: 'angry',
    labelKey: 'tools.textToSpeech.emotions.angry',
    tagBackgroundColor: 'rgba(221, 97, 97, 0.16)',
    tagTextColor: '#F98D8D',
    emotionTextBackgroundColor: 'rgba(221, 97, 97, 0.16)',
  },
  {
    id: 'fearful',
    labelKey: 'tools.textToSpeech.emotions.fearful',
    tagBackgroundColor: 'rgba(87, 242, 123, 0.24)',
    tagTextColor: '#84E79B',
    emotionTextBackgroundColor: 'rgba(87, 242, 123, 0.16)',
  },
  {
    id: 'disgusted',
    labelKey: 'tools.textToSpeech.emotions.disgusted',
    tagBackgroundColor: 'rgba(98, 199, 118, 0.24)',
    tagTextColor: '#62C776',
    emotionTextBackgroundColor: 'rgba(98, 199, 118, 0.16)',
  },
  {
    id: 'surprised',
    labelKey: 'tools.textToSpeech.emotions.surprised',
    tagBackgroundColor: 'rgba(87, 183, 242, 0.24)',
    tagTextColor: '#88D2FF',
    emotionTextBackgroundColor: 'rgba(87, 183, 242, 0.24)',
  },
  {
    id: 'neutral',
    labelKey: 'tools.textToSpeech.emotions.neutral',
    tagBackgroundColor: 'rgba(87, 101, 242, 0.24)',
    tagTextColor: '#969FFF',
    emotionTextBackgroundColor: 'rgba(87, 101, 242, 0.24)',
  },
];

export const CLAW_LOGO_IMAGE_URL =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/viduclaw-logo-55637b9a.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/viduclaw-logo-55637b9a.webp';
export * from './feature-tour';
